import { useState } from "react";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Language as LanguageIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarMonthIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrganization } from "../../context/OrganizationContext";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import { useThemeMode } from "../../context/ThemeContext";
import OrganizationSettingsDialog from "./OrganizationSettingsDialog";

export default function AppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { organization } = useOrganization();
  const { safeNavigate } = useUnsavedChanges();
  const { mode, toggleTheme } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [employeesAnchorEl, setEmployeesAnchorEl] =
    useState<null | HTMLElement>(null);
  const [schedulesAnchorEl, setSchedulesAnchorEl] =
    useState<null | HTMLElement>(null);
  const [shopsAnchorEl, setShopsAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const closeAllNavMenus = () => {
    setEmployeesAnchorEl(null);
    setSchedulesAnchorEl(null);
    setShopsAnchorEl(null);
  };

  const handleEmployeesMenu = (event: React.MouseEvent<HTMLElement>) => {
    closeAllNavMenus();
    setEmployeesAnchorEl(event.currentTarget);
  };

  const handleEmployeesClose = () => {
    setEmployeesAnchorEl(null);
  };

  const handleSchedulesMenu = (event: React.MouseEvent<HTMLElement>) => {
    closeAllNavMenus();
    setSchedulesAnchorEl(event.currentTarget);
  };

  const handleSchedulesClose = () => {
    setSchedulesAnchorEl(null);
  };

  const handleShopsMenu = (event: React.MouseEvent<HTMLElement>) => {
    closeAllNavMenus();
    setShopsAnchorEl(event.currentTarget);
  };

  const handleShopsClose = () => {
    setShopsAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    safeNavigate(path);
    closeAllNavMenus();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth");
    } catch (error) {
      console.error("Logout error:", error);
    }
    handleClose();
  };

  const getInitials = (name: string) => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: "none",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: 3 }}>
        {/* Left Side: Logo and Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          {/* App Logo - clickable to go to landing page */}
          <Box
            onClick={() => navigate("/dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarViewMonthIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              ShiftGrid
            </Typography>
          </Box>

          {/* Navigation Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Dashboard */}
            <Button
              startIcon={<DashboardIcon />}
              onClick={() => handleNavigation("/dashboard")}
              onMouseEnter={closeAllNavMenus}
              sx={{
                color:
                  location.pathname === "/dashboard"
                    ? "primary.main"
                    : "text.primary",
                fontWeight: location.pathname === "/dashboard" ? 600 : 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Dashboard
            </Button>

            {/* Schedules with dropdown */}
            <Button
              startIcon={<CalendarMonthIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onMouseEnter={handleSchedulesMenu}
              onClick={() => handleNavigation("/schedules/employees")}
              sx={{
                color: location.pathname.startsWith("/schedules")
                  ? "primary.main"
                  : "text.primary",
                fontWeight: location.pathname.startsWith("/schedules")
                  ? 600
                  : 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Schedules
            </Button>

            {/* Employees with dropdown */}
            <Button
              startIcon={<PeopleIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onMouseEnter={handleEmployeesMenu}
              onClick={() => handleNavigation("/employees/availability")}
              sx={{
                color: location.pathname.startsWith("/employees")
                  ? "primary.main"
                  : "text.primary",
                fontWeight: location.pathname.startsWith("/employees")
                  ? 600
                  : 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Employees
            </Button>

            {/* Shops with dropdown */}
            <Button
              startIcon={<StoreIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onMouseEnter={handleShopsMenu}
              onClick={() => handleNavigation("/shops/coverage")}
              sx={{
                color: location.pathname.startsWith("/shops")
                  ? "primary.main"
                  : "text.primary",
                fontWeight: location.pathname.startsWith("/shops") ? 600 : 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              Shops
            </Button>
          </Box>
        </Box>

        {/* Right Side Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Dark/Light Mode Toggle */}
          <Tooltip title={mode === 'dark' ? "Light Mode" : "Dark Mode"}>
            <IconButton
              size="medium"
              onClick={toggleTheme}
              sx={{
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Language Selector */}
          <Tooltip title="Language">
            <IconButton
              size="medium"
              onClick={handleLangMenu}
              sx={{
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <LanguageIcon />
            </IconButton>
          </Tooltip>

          {/* Profile Avatar */}
          <Tooltip title="Account">
            <IconButton
              size="medium"
              onClick={handleMenu}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                p: 0.5,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "primary.main",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {user?.displayName ? getInitials(user.displayName) : "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{
            mt: 1,
            "& .MuiPaper-root": {
              minWidth: 240,
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              handleClose();
              setSettingsDialogOpen(true);
            }}
            sx={{ my: 0.5 }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={organization?.name || "Organization"}
              secondary={user?.role?.toUpperCase()}
              secondaryTypographyProps={{
                fontSize: "0.75rem",
              }}
            />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleLogout}
            sx={{ mt: 0.5, color: "error.main" }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        {/* Language Menu */}
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={handleLangClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            mt: 1,
            "& .MuiPaper-root": {
              minWidth: 160,
              borderRadius: 2,
            },
          }}
        >
          <MenuItem onClick={handleLangClose}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="span"
                sx={{
                  fontSize: "1.25rem",
                  lineHeight: 1,
                }}
              >
                🇺🇸
              </Box>
              <Typography>English</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleLangClose}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="span"
                sx={{
                  fontSize: "1.25rem",
                  lineHeight: 1,
                }}
              >
                🇪🇸
              </Box>
              <Typography>Español</Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Employees Menu */}
        <Menu
          anchorEl={employeesAnchorEl}
          open={Boolean(employeesAnchorEl)}
          onClose={handleEmployeesClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          MenuListProps={{
            onMouseLeave: handleEmployeesClose,
          }}
          slotProps={{
            paper: {
              onMouseLeave: handleEmployeesClose,
            },
          }}
          hideBackdrop
          disableScrollLock
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              pointerEvents: "auto",
              minWidth: 150,
              borderRadius: 1,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleNavigation("/employees/availability")}>
            Availability
          </MenuItem>
          <MenuItem onClick={() => handleNavigation("/employees/information")}>
            Information
          </MenuItem>
        </Menu>

        {/* Schedules Menu */}
        <Menu
          anchorEl={schedulesAnchorEl}
          open={Boolean(schedulesAnchorEl)}
          onClose={handleSchedulesClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          MenuListProps={{
            onMouseLeave: handleSchedulesClose,
          }}
          slotProps={{
            paper: {
              onMouseLeave: handleSchedulesClose,
            },
          }}
          hideBackdrop
          disableScrollLock
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              pointerEvents: "auto",
              minWidth: 150,
              borderRadius: 1,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleNavigation("/schedules/employees")}>
            Employees
          </MenuItem>
          <MenuItem onClick={() => handleNavigation("/schedules/shops")}>
            Shops
          </MenuItem>
        </Menu>

        {/* Shops Menu */}
        <Menu
          anchorEl={shopsAnchorEl}
          open={Boolean(shopsAnchorEl)}
          onClose={handleShopsClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          MenuListProps={{
            onMouseLeave: handleShopsClose,
          }}
          slotProps={{
            paper: {
              onMouseLeave: handleShopsClose,
            },
          }}
          hideBackdrop
          disableScrollLock
          sx={{
            pointerEvents: "none",
            "& .MuiPaper-root": {
              pointerEvents: "auto",
              minWidth: 150,
              borderRadius: 1,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleNavigation("/shops/coverage")}>
            Coverage
          </MenuItem>
          <MenuItem onClick={() => handleNavigation("/shops/information")}>
            Information
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Organization Settings Dialog */}
      <OrganizationSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      />
    </MuiAppBar>
  );
}
